import { toQueryParameter, setCookieData } from "@digitallab/grid-common-frontend-components";
import { react18WorkaroundProps } from "../../utils/constants/constants";
import { OwcIconButton, OwcListItem, OwcMenu, OwcTooltip } from "@one/react";
import { isAfter, parseISO } from "date-fns";
import moment from "moment-timezone";
import { MouseEvent, ReactNode, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { ENTRY_TYPE } from "../../constants";
import { RootStore } from "../../reducers";
import { BookingRewireDataType } from "../../Models/BookingRewireDataType";
import { EndMenuItem } from "../my-booking/BookingMenuAction";

const CoverMenuItem = styled.div`
  height: 0.973480224609375px;
  width: 100%;
  border-radius: 0;
  background: var(--one-color-gray-300);
`;

type BookingMenuActionParams = {
  params: {
    data: BookingRewireDataType;
  };
  handlers: {
    deleteEquipment: () => void;
    editBooking: (data: BookingRewireDataType) => void;
    setRowEquipmentId: React.Dispatch<React.SetStateAction<string>>;
    setPopupEnableStatus: React.Dispatch<React.SetStateAction<boolean>>;
    handleApprove?: (data: BookingRewireDataType) => void;
    handleReject?: (data: BookingRewireDataType) => void;
    refreshGrid?: () => void;
    changeEndTimeOpen?: (params: { data: BookingRewireDataType }) => void;
  };
};

type BaseFormParams = {
  description: string;
};

type RunFormParams = BaseFormParams & {
  runStatus: string;
  elnName?: string;
  elnEid?: string;
  elnModifiedAt?: string;
  elnObjectType?: string;
  elnOwner?: string;
  elnState?: string;
  elnType?: string;
};

type ActionFormParams = BaseFormParams & {
  action: string;
};

type FormParams = RunFormParams | ActionFormParams;

export const getLogParamsList = (data: BookingRewireDataType) => {
  const { bookingEntries, description, type, elnId, dateTo } = data;
  const logLinks = [];
  const bookingType = type === "Run" ? "run" : "action";
  const standaloneEquipmentIdList = bookingEntries
    .filter(({ equipment }) => equipment.entryType === ENTRY_TYPE.standaloneEquipment)
    .map(({ inventoryId }) => inventoryId);
  const runStatus = isAfter(new Date(), parseISO(dateTo)) ? "COMPLETED" : "PROCESSING";

  let formParams: FormParams;
  const baseParams: BaseFormParams = { description };
  if (bookingType === "run") {
    formParams = {
      ...baseParams,
      runStatus,
      elnName: elnId?.name,
      elnEid: elnId?.eid,
      elnModifiedAt: elnId?.modifiedAt,
      elnObjectType: elnId?.objectType,
      elnOwner: elnId?.owner,
      elnState: elnId?.state,
      elnType: elnId?.type,
    };
  } else {
    formParams = { ...baseParams, action: type };
  }
  const clusterEquipmentIdList = bookingEntries
    .filter(({ equipment }) => equipment.entryType === ENTRY_TYPE.cluster)
    .map(({ inventoryId }) => inventoryId);
  if (clusterEquipmentIdList.length > 0) {
    clusterEquipmentIdList.forEach((element, index) => {
      const timestamp = new Date().getTime().toString() + index;
      setCookieData(`equipmentIdList${timestamp}`, [element], { expires: 2 / (24 * 60) });
      logLinks.push(`/logs/cluster/${bookingType}${toQueryParameter({ ...formParams, timestamp })}`);
    });
  }
  if (standaloneEquipmentIdList.length > 0) {
    const timestamp = new Date().getTime().toString() + "standalones";
    setCookieData(`equipmentIdList${timestamp}`, standaloneEquipmentIdList);
    logLinks.push(`/logs/${bookingType}${toQueryParameter({ ...formParams, timestamp })}`);
  }
  return logLinks;
};

const BookingMenuAction = ({
  params,
  handlers: {
    deleteEquipment,
    editBooking,
    setRowEquipmentId,
    setPopupEnableStatus,
    handleApprove,
    handleReject,
    changeEndTimeOpen,
  },
}: BookingMenuActionParams) => {
  const user = useSelector((store: RootStore) => store.user);
  const [anchorEl, setAnchorEl] = useState<HTMLOwcIconButtonElement | null>(null);
  const uniqueAnchorId = `booking-menu-${params?.data?.id}`;
  const currentTimeString = moment.tz(user.timezone).utc().format();

  const handleSettingClose = () => {
    setAnchorEl(null);
  };
  const handleSettingClick = (
    event: MouseEvent<HTMLOwcIconButtonElement, globalThis.MouseEvent>,
    params: { data: BookingRewireDataType },
  ) => {
    setRowEquipmentId?.(params?.data?.id);
    setAnchorEl(event.currentTarget);
  };

  const userDoesNotHaveRightsToWriteInLogbook =
    !user.roles.includes("LogbookAdmin") && !user.roles.includes("LogbookWriter");

  // Check if booking is pending approval and user is admin
  const hasHazardousMaterials = params?.data?.hazardousDeclaration?.hasHazardousMaterials;
  const requiresAssistance = params?.data?.assistanceDeclaration?.requiresAssistance;
  const approvalStatus = params?.data?.approval?.status?.toString().toUpperCase();
  const isPendingApproval =
    (hasHazardousMaterials || requiresAssistance) && (!params?.data?.approval || approvalStatus === "PENDING");
  const isAdmin = user?.roles?.some((role: string) => role === "BookingAdmin");

  return (
    <>
      <OwcIconButton
        icon="more_vertical"
        type="legacy"
        flat
        onClick={(event) => {
          handleSettingClick(event, params);
        }}
        id={uniqueAnchorId}
        data-testid="booking-menu-toggle"
        disabled={!params?.data?.active}
        {...react18WorkaroundProps}
      />
      {ReactDOM.createPortal(
        (
          <OwcMenu
            data-testid="simple-menu"
            scrollPadding={false}
            anchor={uniqueAnchorId}
            visible={!!anchorEl}
            onClickOutside={handleSettingClose}
            disablePortal={true}
            {...react18WorkaroundProps}
          >
            <OwcListItem
              data-testid="simple-menu-filter"
              onClick={() => {
                setAnchorEl(null);
                setPopupEnableStatus(true);
                editBooking(params?.data);
              }}
              disabled={
                isAdmin
                  ? false
                  : !params?.data?.active ||
                    params?.data?.checkInDone ||
                    (params?.data?.checkInRequired && params.data.checkInStartsAt <= new Date().getTime() / 1000) ||
                    params?.data?.dateTo < currentTimeString
              }
              {...react18WorkaroundProps}
            >
              Edit
            </OwcListItem>
            <div id={`AddLogMenuOption-${params?.data?.id}`}>
              <OwcListItem
                data-testid="simple-menu-filter"
                onClick={() => {
                  setAnchorEl(null);
                  const logParamsList = getLogParamsList(params.data);
                  logParamsList.forEach((url, index) => {
                    window.open(`${process.env.REACT_APP_LOGBOOK_APP_URL}${url}`, index.toString());
                  });
                }}
                disabled={userDoesNotHaveRightsToWriteInLogbook}
                {...react18WorkaroundProps}
              >
                Add log
              </OwcListItem>
              {userDoesNotHaveRightsToWriteInLogbook && (
                <OwcTooltip anchor={`AddLogMenuOption-${params?.data?.id}`} {...react18WorkaroundProps}>
                  You do not have permissions to create log entry.
                </OwcTooltip>
              )}
            </div>
            <CoverMenuItem />
            <EndMenuItem
              setAnchorEl={setAnchorEl}
              deleteEquipment={deleteEquipment}
              params={params}
              currentTimeString={currentTimeString}
              changeEndTimeOpen={changeEndTimeOpen}
            />
            <OwcListItem
              data-testid="simple-menu-filter"
              onClick={() => {
                setAnchorEl(null);
                deleteEquipment();
              }}
              disabled={
                isAdmin
                  ? !params?.data?.active
                  : !params?.data?.active ||
                    params?.data?.dateFrom < currentTimeString ||
                    params?.data?.dateTo < currentTimeString
              }
              {...react18WorkaroundProps}
            >
              Cancel
            </OwcListItem>
            {isPendingApproval && isAdmin && (
              <>
                <CoverMenuItem />
                <OwcListItem
                  {...({
                    "data-testid": "simple-menu-approve",
                    onClick: () => {
                      setAnchorEl(null);
                      if (handleApprove) {
                        handleApprove(params?.data);
                      }
                    },
                    disabled: !params?.data?.active,
                  } as any)}
                >
                  Approve
                </OwcListItem>
                <CoverMenuItem />
                <OwcListItem
                  {...({
                    "data-testid": "simple-menu-reject",
                    onClick: () => {
                      setAnchorEl(null);
                      if (handleReject) {
                        handleReject(params?.data);
                      }
                    },
                    disabled: !params?.data?.active,
                  } as any)}
                >
                  Reject
                </OwcListItem>
              </>
            )}
          </OwcMenu>
        ) as ReactNode,
        document.body,
      )}
    </>
  );
};
export default BookingMenuAction;
